import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { formatRatingChange } from '../services/eloService';

const GameResultPopup = ({ gameResult, onDismiss }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(-100)).current;

  useEffect(() => {
    // Slide in and fade in
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        friction: 8,
        tension: 40,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto-dismiss after 4 seconds
    const dismissTimer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: -100,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(() => {
        if (onDismiss) onDismiss();
      });
    }, 4000);

    return () => clearTimeout(dismissTimer);
  }, []);

  const { result, ratingChange, newRating, gameType } = gameResult;

  const isWin = result === 'win';
  const isLoss = result === 'loss';
  const isDraw = result === 'draw';
  const isDispute = result === 'dispute';

  const getResultConfig = () => {
    if (isWin) {
      return {
        title: 'Victory!',
        icon: 'emoji-events',
        iconColor: '#FFD700',
        bgColor: 'rgba(46, 125, 50, 0.95)',
        xp: '+50 XP',
      };
    } else if (isLoss) {
      return {
        title: 'Defeat',
        icon: 'close',
        iconColor: '#FF6B6B',
        bgColor: 'rgba(211, 47, 47, 0.95)',
        xp: '+10 XP',
      };
    } else if (isDraw) {
      return {
        title: 'Draw',
        icon: 'people',
        iconColor: '#94EB98',
        bgColor: 'rgba(66, 66, 66, 0.95)',
        xp: '+25 XP',
      };
    } else if (isDispute) {
      return {
        title: 'Dispute',
        icon: 'warning',
        iconColor: '#FFC107',
        bgColor: 'rgba(255, 152, 0, 0.95)',
        xp: '',
      };
    } else {
      return {
        title: 'Game Complete',
        icon: 'check-circle',
        iconColor: '#94EB98',
        bgColor: 'rgba(66, 66, 66, 0.95)',
        xp: '',
      };
    }
  };

  const config = getResultConfig();

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View style={[styles.popup, { backgroundColor: config.bgColor }]}>
        {/* Result Icon & Title */}
        <View style={styles.header}>
          <Icon name={config.icon} size={48} color={config.iconColor} />
          <Text style={styles.title}>{config.title}</Text>
        </View>

        {/* XP Gain */}
        {config.xp && (
          <View style={styles.xpContainer}>
            <Icon name="star" size={20} color="#FFD700" />
            <Text style={styles.xpText}>{config.xp}</Text>
          </View>
        )}

        {/* Rating Change (only for rated games) */}
        {gameType === 'RATED' && ratingChange !== 0 && (
          <View style={styles.ratingContainer}>
            <Text style={styles.ratingLabel}>Rating:</Text>
            <Text style={styles.ratingValue}>
              {newRating} {formatRatingChange(ratingChange)}
            </Text>
          </View>
        )}

        {/* Casual Game Indicator */}
        {gameType === 'CASUAL' && (
          <Text style={styles.casualText}>Casual Game</Text>
        )}

        {/* Dispute Message */}
        {isDispute && (
          <Text style={styles.disputeText}>
            Results don't match. No stats or rating changes applied.
          </Text>
        )}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: Platform.OS === 'web' ? 100 : 80,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 99999,
    pointerEvents: 'none',
  },
  popup: {
    paddingHorizontal: 32,
    paddingVertical: 24,
    borderRadius: 20,
    alignItems: 'center',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 12,
    minWidth: 280,
    maxWidth: '90%',
  },
  header: {
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: 'white',
    marginTop: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  xpContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
  },
  xpText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFD700',
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  ratingLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  ratingValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  casualText: {
    fontSize: 14,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 8,
  },
  disputeText: {
    fontSize: 13,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 12,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
});

export default GameResultPopup;
